import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { useState } from 'react'
import { ChevronDown, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'

export const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink shadow-[0_1px_2px_rgba(15,23,42,0.04)] outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 aria-[invalid=true]:border-rose-400 aria-[invalid=true]:focus:ring-rose-500/10'

interface FieldProps {
    label: string
    htmlFor: string
    required?: boolean
    error?: string | null
    hint?: string
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, htmlFor, required, error, hint, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={htmlFor} className='mb-1.5 block text-[13px] font-semibold text-slate-800'>
                {label}

                {required && <span aria-hidden='true' className='text-rose-500'> *</span>}
            </label>

            {children}

            {error ? (
                <p id={`${htmlFor}-error`} className='mt-1.5 text-xs font-medium text-rose-600'>
                    {error}
                </p>
            ) : hint ? (
                <p id={`${htmlFor}-hint`} className='mt-1.5 text-xs text-slate-500'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

interface ControlProps {
    invalid?: boolean
    describedBy?: string
}

export const TextInput: FC<InputHTMLAttributes<HTMLInputElement> & ControlProps> = ({ invalid, describedBy, className, ...props }): ReactNode => {
    return (
        <input
            {...props}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(inputClass, className)}
        />
    )
}

export const PasswordInput: FC<InputHTMLAttributes<HTMLInputElement> & ControlProps> = ({ invalid, describedBy, className, ...props }): ReactNode => {
    const [visible, setVisible] = useState(false)

    return (
        <div className='relative'>
            <input
                {...props}
                type={visible ? 'text' : 'password'}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                className={cn(inputClass, 'pr-11', className)}
            />

            <button
                type='button'
                onClick={() => setVisible(value => !value)}
                aria-label={visible ? 'Hide password' : 'Show password'}
                className='absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-ink'
            >
                {visible ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
            </button>
        </div>
    )
}

export const SelectInput: FC<SelectHTMLAttributes<HTMLSelectElement> & ControlProps> = ({ invalid, describedBy, className, children, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select
                {...props}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                className={cn(inputClass, 'appearance-none pr-10', className)}
            >
                {children}
            </select>

            <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-slate-400' />
        </div>
    )
}

export const TextArea: FC<TextareaHTMLAttributes<HTMLTextAreaElement> & ControlProps> = ({ invalid, describedBy, className, ...props }): ReactNode => {
    return (
        <textarea
            {...props}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            className={cn(inputClass, 'min-h-24 resize-y', className)}
        />
    )
}

export const passwordStrength = (password: string): { score: number, label: string } => {
    let score = 0

    if (password.length >= 8) score += 1
    if (password.length >= 12) score += 1
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1
    if (/\d/.test(password)) score += 1
    if (/[^A-Za-z0-9]/.test(password)) score += 1

    const capped = Math.min(4, score)

    return { score: capped, label: ['Too short', 'Weak', 'Fair', 'Good', 'Strong'][capped] }
}
